import React, { useState,useRef } from "react";
import PropTypes from "prop-types";
import { Button, CircularProgress, Stack } from "@mui/material";
import { UploadFile, Download, Refresh } from "@mui/icons-material";
import ExcelJS from "exceljs";
import { axios_tenant } from "../../../axios";
import {toast} from "react-toastify";
import { METADATA_TEMPLATE_HEADERS, normalizeCell, hasValidTemplateHeaders, TEMPLATE_MISMATCH_MESSAGE } from "./metadataTemplate";

const UploadMetadata = ({loadRequests, loadingRequests}) => {
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null); // Ref to reset the file input

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setLoading(true);

    try {
      // Read and process the Excel file
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(await file.arrayBuffer());
      const worksheet = workbook.getWorksheet(1);

      if (!hasValidTemplateHeaders(worksheet)) {
        toast.error(TEMPLATE_MISMATCH_MESSAGE);
        return;
      }

      const metadataArray = [];
      let hasMissingFields = false;
      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber > 1) { // Skip header
          const rowData = row.values.slice(1); // Remove empty first element
          const metadata = {
            patentNumber: rowData[0],
            country: rowData[1]?.toUpperCase(),
            kindCode: rowData[2]?.toUpperCase(),
            grantDate: normalizeCell(rowData[3]),
            applicantName: normalizeCell(rowData[4]),
            inventor: normalizeCell(rowData[5]),
            applicationFilingDate: normalizeCell(rowData[6]),
            applicationNumber: normalizeCell(rowData[7]),
            abstractOrTranslation: normalizeCell(rowData[8]),
          };
          const requiredFields = [
            // { key: "applicantName", label: "Applicant Name" },
            { key: "inventor", label: "Inventor" },
            // { key: "applicationFilingDate", label: "Application Filing Date" },
            // { key: "applicationNumber", label: "Application Number" }
          ];

          if(!metadata.patentNumber){
            toast.error(`Missing Reference Number in Row ${rowNumber}`);
            hasMissingFields = true
            return
          } 
          if (!metadata.country){
            toast.error(`Missing Country in Row ${rowNumber}`);
            hasMissingFields = true
            return
          }
          if (!metadata.kindCode){
            toast.error(`Missing Kind Code in Row ${rowNumber}`);
            hasMissingFields = true
            return
          }
          
          const missingFields = requiredFields
            .filter(field => !metadata[field.key])
            .map(field => field.label);
            
          if (missingFields.length > 0) {
            toast.error(`Missing fields (${missingFields.join(", ")}) for ${metadata.country}${metadata.patentNumber}${metadata.kindCode}`);
            hasMissingFields = true
            return;
          }
          
          metadataArray.push(metadata);
          // Validate grantDate
          // if (metadata.grantDate && isNaN(Date.parse(metadata.grantDate))) {
          //   toast.error(`Invalid Grant Date for Patent ${metadata.patentNumber} in Country ${metadata.country} (Row ${rowNumber})`);
          //   hasMissingFields = true
          //   return;
          // }
          // Validate applicationFilingDate
          // if (metadata.applicationFilingDate && isNaN(Date.parse(metadata.applicationFilingDate))) {
          //   toast.error(`Invalid Application Filing Date for Patent ${metadata.patentNumber} in Country ${metadata.country} (Row ${rowNumber})`);
          //   hasMissingFields = true
          //   return;
          // }
        }
        
      });
      if(hasMissingFields) return; 
      if (metadataArray.length === 0) {
        toast.error("No valid metadata found in the file");
        return;
      }

      // Send metadata to API
      const response = await axios_tenant.post('/reference-downloader/metadata', {
        metadata: metadataArray
      });

      if (response.status == 200){
        toast.success("Metadata uploaded successfully!");
      }

      await loadRequests();
    } catch (error) {
      toast.error("Error uploading metadata");
      console.error("Metadata upload failed", error);
    } finally {
      setLoading(false);
      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = null;
      }
    }
  };


  const handleDownloadSample = () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Metadata");

    // Add headers to the worksheet
    worksheet.addRow(METADATA_TEMPLATE_HEADERS);

    // Adjust column widths (optional)
    worksheet.columns = [
      { width: 15 },
      { width: 15 },
      { width: 15 },
      { width: 20 },
      { width: 25 },
      { width: 25 },
      { width: 20 },
      { width: 25 },
      { width: 25 },
    ];

    // Apply dropdown validation to the entire Column 'I' (Abstract/Translation File?)
    worksheet.dataValidations.add("I2:I1048576", {
      type: "list",
      allowBlank: true,
      formulae: ['"Abstract,Translation"'],
      showErrorMessage: true,
      errorTitle: "Invalid Input",
      error: "Only 'Abstract' or 'Translation' is allowed.",
  });

    // Generate Excel file and trigger download
    workbook.xlsx.writeBuffer().then((buffer) => {
      const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "Metadata_Sample.xlsx";
      link.click();
      URL.revokeObjectURL(url); // Clean up the URL
    });
  };

  return (
    <Stack direction="row" spacing={2}>
        <input
        accept=".xlsx"
        style={{ display: "none" }}
        id="upload-metadata-file"
        type="file"
        onChange={handleFileUpload}
        ref={fileInputRef} // Attach the ref to the input
      />
      <label htmlFor="upload-metadata-file">
        <Button
          variant="outlined"
          startIcon={loading ? <CircularProgress size={20} /> : <UploadFile />}
          component="span"
          disabled={loading}
        >
          {loading ? "Uploading..." : "Upload Metadata"}
        </Button>
      </label>
      <Button
          variant="outlined"
          startIcon={<Download />}
          onClick={handleDownloadSample}
        >
          Metadata Template
        </Button>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          disabled={loadingRequests}
          onClick={loadRequests}
        >
          Reload
        </Button>
    </Stack>
  );
};

UploadMetadata.propTypes = {
  loadRequests: PropTypes.func,
  loadingRequests: PropTypes.bool,
};

export default UploadMetadata;